import {
  Button,
  Card,
  Col,
  Divider,
  EditButton,
  Icons,
  ListButton,
  Popconfirm,
  Row,
  Show,
  Table,
  TableProps,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useNavigation, useOne } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { ProductFooter } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { EStatus } from 'enums';
import { IPurchaseStockCorrection } from 'interfaces';
import { useState } from 'react';
import { deletePurchaseStockCorrection } from 'services/purchase';
import { formatCurrency } from 'utilities/shared';

const { Title, Text } = Typography;

export const PurchaseStockCorrectionShow = () => {
  const { list } = useNavigation();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const { id } = useParams();
  const { data, isLoading } = useOne<IPurchaseStockCorrection>({
    resource: 'stockCorrectionPurchase',
    id: id || '',
    metaData: {
      fields: [
        'id',
        'purchaseAddress',
        'note',
        'purchaseStatus',
        'vatAmount',
        'shipmentCost',
        'discountCost',
        'subTotal',
        {
          supplierPoc: ['id', 'name'],
        },
        {
          warehouse: ['id'],
        },
        'totalAmount',
        {
          supplier: ['id', 'name'],
        },
        {
          stockCorrectionPurchaseProducts: [
            'id',
            'quantity',
            'unitPrice',
            'subTotal',
            {
              productVariant: [
                'id',
                'name',
                'sku',
                {
                  productCopy: ['name'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  const { data: canAccessDelete } = useCan({
    resource: 'purchases',
    action: 'delete',
    params: { field: 'id' },
  });

  const purchaseStockCorrection = data?.data;
  const note = purchaseStockCorrection?.note;
  const supplierPoc = purchaseStockCorrection?.supplierPoc;
  const subTotal = purchaseStockCorrection?.subTotal ?? 0;
  const vatPercentage = purchaseStockCorrection?.vatAmount ?? 0;
  const vatAmount = Number((subTotal * (vatPercentage / 100)).toFixed(2));
  const shipmentCost = purchaseStockCorrection?.shipmentCost ?? 0;
  const discountCost = purchaseStockCorrection?.discountCost ?? 0;
  const netTotal = purchaseStockCorrection?.totalAmount ?? 0;
  const purchaseStockCorrections = purchaseStockCorrection?.stockCorrectionPurchaseProducts.map(
    (purchase) => ({
      id: purchase.id,
      sku: purchase.productVariant.sku,
      variantName: purchase.productVariant.name,
      productName: purchase.productVariant.productCopy.name,
      quantity: purchase.quantity,
      unitPrice: purchase.unitPrice,
      subTotal: formatCurrency(purchase.subTotal),
    })
  );

  const isCompleted = purchaseStockCorrection?.purchaseStatus === EStatus.COMPLETED;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    try {
      await deletePurchaseStockCorrection(id);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a purchase stock correction',
      });
      list('stock-correction-purchases');
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete purchase stock correction',
      });
    }
  };

  const pageHeader = !isLoading ? (
    <>
      <ListButton />
      <EditButton hidden />
      <Popconfirm
        title="Are you sure?"
        okText="Delete"
        onConfirm={() => handleDelete(id)}
        disabled={!canAccessDelete?.can}
      >
        <Button
          size="middle"
          danger
          icon={<Icons.DeleteOutlined />}
          disabled={!canAccessDelete?.can}
          hidden={isCompleted}
        >
          Delete
        </Button>
      </Popconfirm>
    </>
  ) : null;

  const footer = () => {
    return (
      <ProductFooter
        subTotal={subTotal || 0}
        vatAmount={vatAmount}
        vatPercentage={vatPercentage || 0}
        shipmentCost={shipmentCost || 0}
        netTotal={netTotal || 0}
        discountCost={discountCost || 0}
      />
    );
  };

  const onTableChange: TableProps<any>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  const onPageChange: any = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  // Function to calculate the current page data
  const getCurrentPageData = () => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return purchaseStockCorrections?.slice(startIndex, endIndex);
  };

  return (
    <>
      <Show isLoading={isLoading} headerProps={{ extra: pageHeader }}>
        {/* Purchase Info */}
        <Row gutter={[32, 32]}>
          <Col xs={24} md={24} lg={24}>
            <Card title="Purchase Stock Correction Information">
              <Title level={5}>Supplier Name</Title>
              <Text>{purchaseStockCorrection?.supplier.name}</Text>
              <Title level={5}>Point Of Contact</Title>
              <Text>{supplierPoc?.name}</Text>
              <Title level={5}>Shipping Address</Title>
              <Text>{purchaseStockCorrection?.purchaseAddress}</Text>
              <Title level={5}>Note</Title>
              <Text>{note}</Text>
            </Card>
          </Col>
        </Row>
      </Show>

      <Divider />

      {/* Products Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <CustomTableWithPagination
            total={purchaseStockCorrections?.length || 0}
            page={page}
            limit={limit}
            onPageChange={onPageChange}
          >
            <Table
              // dataSource={purchases}
              dataSource={getCurrentPageData()} // Pass sliced data
              rowKey="id"
              scroll={{ x: true }}
              footer={(_data) => footer()}
              pagination={false}
              onChange={onTableChange}
            >
              <Table.Column dataIndex="sku" title="SKU" />
              <Table.Column dataIndex="variantName" title="Variant Name" />
              <Table.Column dataIndex="productName" title="Product Name" />
              <Table.Column dataIndex="quantity" title="Quantity" />
              <Table.Column dataIndex="unitPrice" title="Unit Price" />
              <Table.Column dataIndex="subTotal" title="Total" />
            </Table>
          </CustomTableWithPagination>
        </Col>
      </Row>
    </>
  );
};
